import { AccountManagement } from "@/interfaces/accountManagement";
import { HttpQuery } from "@/interfaces/httpQuery";
import { User } from "@/interfaces/user";
import { policyList } from "@/internal";
import { UserTableMetadata } from "@/metadata/user";
import { messagesService } from "@/services/messages.service";
import { USER_SERVICE } from "@/services/refactor/users.service";
import { Callback } from "@/types/types";
import { INITIAL_QUERY_STATE } from "@/utils/constants";
import {
  ActionsSubheaderComponent,
  BooleanCheck,
  TableAction,
  TableComponent,
  TableHeader,
  TablePagination
} from "helix-vue-components";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import Template from "./UserManagementList.template.vue";

@Component({
  mixins: [Template],
  components: {
    TableComponent,
    ActionsSubheaderComponent
  }
})
export default class UserManagementListComponent extends Vue {
  @Getter("availability", { namespace: "AccountManagementModule" })
  public availability!: AccountManagement.Account;
  @Getter("hasPermission", { namespace: "authModule" })
  public hasPermission!: BooleanCheck;
  public viewUserPermission: boolean = false;
  public editUserPermission: boolean = false;
  @Action("loadAvailability", { namespace: "AccountManagementModule" })
  protected loadAvailability!: Callback;
  protected users: User[] = [];
  protected pagination: TablePagination | null = null;
  protected loading = true;
  protected generalActions = USER_SERVICE.getGeneralActions();
  protected rowActions!: TableAction[];
  protected headers: TableHeader[] = UserTableMetadata;
  protected colors = ["white", "#f2f2f2", "#f2f2f2"];
  protected query: HttpQuery = INITIAL_QUERY_STATE;

  /**
   * Update requests on table page change event.
   * @param pagination: TablePagination
   */
  public onChangePage(pagination: TablePagination) {
    this.triggerSearch({
      sort: this.query.sort,
      page: pagination.currentPage,
      per_page: pagination.itemsPerPage
    });
  }

  /**
   * Update requests on column name click to sort.
   * @param header: TableHader
   */
  public filter(header: TableHeader) {
    this.triggerSearch({
      ...INITIAL_QUERY_STATE,
      sort: USER_SERVICE.sortQuery(header)
    });
  }

  /**
   * Updates table data based on search component.
   * @param term: string
   */
  public onSearch(term: string) {
    this.triggerSearch({
      ...INITIAL_QUERY_STATE,
      "q[first_name_or_last_name_or_nickname_or_middle_name_or_email_contains]": term
    });
  }

  /**
   * Takes action on serch bar close event.
   * @param update: boolean
   */
  public onClose(update: boolean) {
    if (update) {
      this.triggerSearch(INITIAL_QUERY_STATE);
    }
  }

  private async created() {
    await this.loadAvailability();
    this.triggerSearch();
  }
  private async mounted() {
    this.editUserPermission = this.hasPermission(policyList.modifyUsers);
    this.rowActions = USER_SERVICE.getRowActions(
      this.triggerSearch,
      this.editUserPermission
    );
  }
  private async triggerSearch(query?: HttpQuery) {
    this.loading = true;
    this.query = query || this.query;
    this.query.type = "USER";
    try {
      this.users = await USER_SERVICE.getUsers(this.query);
      this.pagination = await USER_SERVICE.getPagination();
    } catch (error) {
      messagesService.renderErrorMessage(error);
    } finally {
      this.loading = false;
    }
  }
}
